/**
 * d-collection-sort-availability.js
 *
 * 1) Ordena os produtos da coleção colocando disponíveis antes dos esgotados.
 * 2) Substitui a paginação por um botão "Carregar mais" + contador dinâmico
 *    ("Exibindo X de Y produtos"), sem recarregar a página.
 *
 * Para coleções com múltiplas páginas, busca todas as páginas em paralelo,
 * une/ordena os cards e revela `perPage` produtos por clique. Banners editoriais
 * (li.d-collection-banner-item) NÃO são contados como produtos e são reinseridos
 * na linha configurada (data-banner-after).
 *
 * ── Pré-requisitos no HTML ───────────────────────────────────────────────────
 *   1. <ul id="product-grid" data-id data-per-page data-total-pages> … </ul>
 *   2. Cada <li> de produto: data-product-available="{{ product.available }}"
 *   3. Cada <li> de banner:  class="… d-collection-banner-item" data-banner-after="N"
 *   4. Container externo id="ProductGridContainer" (observado p/ re-render de filtros)
 *   5. Botão:   <button data-load-more> … </button>
 *   6. Contador: <element data-collection-count>
 */

(function () {
  'use strict';

  // ─── Selectors / config ────────────────────────────────────────────────────
  var GRID_ID           = 'product-grid';
  var GRID_CONTAINER_ID = 'ProductGridContainer';
  var BANNER_CLASS      = 'd-collection-banner-item';
  var LOAD_MORE_SEL     = '[data-load-more]';
  var COUNT_SEL         = '[data-collection-count]';
  var MAX_PAGES         = 20;

  // ─── State ─────────────────────────────────────────────────────────────────
  var state = {
    products   : [],   // { html, soldOut }[]
    banners    : [],   // { html, pos }[]  (pos = nº de produtos antes do banner)
    perPage    : 8,
    shown      : 0,
    sectionId  : '',
    totalPages : 1,
    fetching   : false,
    initialized: false,
    generation : 0,
  };

  // ─── Metadata from #product-grid ───────────────────────────────────────────
  function readMeta() {
    var el = document.getElementById(GRID_ID);
    if (!el) return null;
    return {
      sectionId : el.dataset.id,
      perPage   : parseInt(el.dataset.perPage, 10)    || 8,
      totalPages: parseInt(el.dataset.totalPages, 10) || 1,
    };
  }

  // ─── URL builder — preserves existing filter/sort params ───────────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  // ─── Fetch all server pages in parallel ────────────────────────────────────
  function fetchAllPages(sectionId, totalPages) {
    var count = Math.min(totalPages, MAX_PAGES);
    var fetches = [];
    for (var i = 1; i <= count; i++) {
      fetches.push(fetch(buildUrl(i, sectionId)).then(function (r) { return r.text(); }));
    }
    return Promise.all(fetches);
  }

  // ─── Split a list of <li> into products and banners ────────────────────────
  function splitItems(liList, collectBanners) {
    var products = [];
    var productCount = 0;
    liList.forEach(function (li) {
      if (li.classList.contains(BANNER_CLASS)) {
        if (collectBanners) {
          var after = parseInt(li.getAttribute('data-banner-after'), 10);
          if (isNaN(after)) after = productCount;
          state.banners.push({ html: li.outerHTML, pos: after });
        }
        return;
      }
      products.push({
        html   : li.outerHTML,
        soldOut: li.getAttribute('data-product-available') === 'false',
      });
      productCount++;
    });
    return products;
  }

  function extractFromHtml(html, collectBanners) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var list = Array.from(doc.querySelectorAll('#' + GRID_ID + ' > li'));
    return splitItems(list, collectBanners);
  }

  // ─── Sort: available first, sold-out last (stable) ─────────────────────────
  function sortProducts(products) {
    var available = products.filter(function (c) { return !c.soldOut; });
    var soldOut   = products.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  // ─── Banner HTML to inject after `n` products already rendered ─────────────
  function bannersAt(n) {
    var total = state.products.length;
    return state.banners
      .filter(function (b) {
        var p = b.pos > total ? total : b.pos;
        return p === n;
      })
      .map(function (b) { return b.html; })
      .join('');
  }

  // ─── Build HTML for products in [from, to) with banners spliced in ─────────
  function sliceHtml(from, to) {
    var html = '';
    if (from === 0) html += bannersAt(0);
    for (var i = from; i < to; i++) {
      html += state.products[i].html;
      html += bannersAt(i + 1);
    }
    return html;
  }

  // ─── Force appended items to be visible (skip scroll-trigger hiding) ───────
  function revealNewChildren(grid, startIndex) {
    var children = Array.from(grid.children);
    for (var i = startIndex; i < children.length; i++) {
      var li = children[i];
      li.classList.remove('scroll-trigger');
      li.style.opacity = '';
      li.style.setProperty('--animation-order', i + 1);
    }
  }

  // ─── Counter + button visibility ───────────────────────────────────────────
  function updateCount() {
    var total = state.products.length;
    var shown = Math.min(state.shown, total);
    var text  = 'Exibindo ' + shown + ' de ' + total + ' produtos';
    document.querySelectorAll(COUNT_SEL).forEach(function (el) {
      el.textContent = text;
    });
  }

  function toggleButton() {
    var visible = state.shown < state.products.length;
    document.querySelectorAll(LOAD_MORE_SEL).forEach(function (btn) {
      btn.hidden = !visible;
    });
  }

  // ─── Reveal the next `perPage` products (append, no reset) ──────────────────
  function loadMore() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    var from = state.shown;
    var to   = Math.min(from + state.perPage, state.products.length);
    if (to <= from) return;

    var startIndex = grid.children.length;
    grid.insertAdjacentHTML('beforeend', sliceHtml(from, to));
    revealNewChildren(grid, startIndex);

    state.shown = to;
    updateCount();
    toggleButton();
  }

  // ─── Initial render: rebuild grid from sorted data, show first page ────────
  function renderInitial() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    grid.innerHTML = '';
    state.shown = 0;
    loadMore();
    if (grid) grid.style.visibility = '';
  }

  // ─── Orchestrator ──────────────────────────────────────────────────────────
  function init() {
    var meta = readMeta();
    if (!meta) return;

    state.sectionId  = meta.sectionId;
    state.perPage    = meta.perPage;
    state.totalPages = meta.totalPages;
    state.products   = [];
    state.banners    = [];

    // Single page: everything is already in the DOM — sort + count in place.
    if (meta.totalPages <= 1) {
      var grid = document.getElementById(GRID_ID);
      if (!grid) return;
      state.products = sortProducts(splitItems(Array.from(grid.children), true));
      state.initialized = true;
      renderInitial();
      return;
    }

    // Multi page: fetch all pages, sort, then reveal perPage at a time.
    var gen = ++state.generation;
    state.fetching = true;
    state.initialized = false;

    var gridEl = document.getElementById(GRID_ID);
    if (gridEl) gridEl.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // stale
        var all = [];
        pages.forEach(function (html, idx) {
          all = all.concat(extractFromHtml(html, idx === 0));
        });
        state.products    = sortProducts(all);
        state.fetching    = false;
        state.initialized = true;
        renderInitial();
      })
      .catch(function (err) {
        console.warn('[d-collection] load-more fetch failed, keeping server render.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        if (gridEl) gridEl.style.visibility = '';
      });
  }

  // ─── Event delegation (survives filter/sort AJAX re-renders) ───────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest(LOAD_MORE_SEL);
    if (!btn) return;
    e.preventDefault();
    loadMore();
  });

  // ─── MutationObserver — detect Dawn's facets/sort AJAX updates ─────────────
  var _debounce = null;
  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      var gridInserted = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.id === GRID_ID
            || (typeof n.querySelector === 'function' && !!n.querySelector('#' + GRID_ID));
        });
      });
      if (!gridInserted || state.fetching) return;

      clearTimeout(_debounce);
      _debounce = setTimeout(init, 150);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Boot ──────────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    init();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
